/* eslint-disable max-lines -- flag-gated live-transcription variant coexists
   with the legacy auto-fill step until the flag is fully rolled out. */
import { useIsMicTestLiveTranscriptionEnabled } from '@/features/flags';
import {
  ErrorSeverity,
  ErrorType,
  OnboardingErrorStep,
} from '@/models/analytics/features/errors';
import { useOnboardingFieldTracking } from '@/models/analytics/features/useOnboardingFieldTracking';
import { SupportedLocale } from '@/utils/i18n/supportedLocales';
import { useTranslations } from '@/utils/translations';
import { Button } from '@heidi/design-system/button';
import {
  TypographyCaption,
  TypographyH2,
  TypographyP,
  TypographyP2,
} from '@heidi/design-system/typography';
import {
  AudioBars,
  FALLBACK_SAMPLE_TEXT,
  MicTestReadyState,
  OnboardingStepShell,
  transcriptDialogueWords,
  useMicStream,
  useSimulatedTranscription,
} from '@heidi/growth';
import { FieldType } from '@heidi/runtime/analytics-vocabulary';
import { ArrowRight, Check, Mic, SkipForward, Square } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalizedSpecialtyName } from '../../../hooks/useLocalizedSpecialtyName';

interface MicTestStepProps {
  onContinue: () => void;
  onSkip: () => void;
  onTranscriptChange: (transcript: string) => void;
  onRecordingDurationChange?: (seconds: number) => void;
  /** Short specialty-specific script to read aloud. Falls back to a generic sample. */
  sampleText?: string;
  /**
   * Gate the Continue button until the user has produced a transcript (via the
   * auto-fill shortcut or their own voice). Set to `false` once the user has
   * completed the mic-test once so revisits (e.g. via Back navigation) don't
   * re-block them.
   */
  requireRecordingToContinue?: boolean;
}

type MicState = 'idle' | 'recording' | 'done';

// Auto-play streams the sample over a fixed duration regardless of script length
// (long specialty dialogues were ~90 ms/word and felt sluggish).
const LIVE_TRANSCRIPTION_PLAYBACK_MS = 5000;
const LIVE_TRANSCRIPTION_START_DELAY_MS = 150;
/** Personalised specialty-in-title copy is English-only; other locales use generic. */
const isEnglishMicTestLocale = (activeLocale: SupportedLocale): boolean =>
  activeLocale === SupportedLocale.En || activeLocale === SupportedLocale.EnPs;

export function MicTestStep({
  onContinue,
  onSkip,
  onTranscriptChange,
  onRecordingDurationChange,
  sampleText,
  requireRecordingToContinue = true,
}: MicTestStepProps) {
  const { t, locale } = useTranslations();
  const { trackOnce, trackError } = useOnboardingFieldTracking('mic_test');
  // Way-6 "live transcription" treatment: name the clinician's specialty in the
  // heading, framing the sample consult.
  const liveTranscriptionEnabled = useIsMicTestLiveTranscriptionEnabled();
  const specialtyName = useLocalizedSpecialtyName();
  const [micState, setMicState] = useState<MicState>('idle');
  const [revealedWords, setRevealedWords] = useState(0);
  const [autofilled, setAutofilled] = useState(false);
  const recordingStartRef = useRef<number | null>(null);
  const autofillDoneRef = useRef<HTMLDivElement>(null);

  const sampleWords = useMemo(() => {
    const rawText =
      sampleText && sampleText.trim().length > 0 ? sampleText : FALLBACK_SAMPLE_TEXT;
    // Label-free dialogue words, aligned with the preview's turn render.
    return transcriptDialogueWords(rawText);
  }, [sampleText]);

  const mic = useMicStream();
  const { stopMonitoring } = mic;
  const isRecording = micState === 'recording';

  const handleTranscriptChange = useCallback(
    (text: string) => {
      const count = text.split(' ').filter(Boolean).length;
      setRevealedWords(count);
      onTranscriptChange(text);
    },
    [onTranscriptChange]
  );

  const sim = useSimulatedTranscription(
    mic.isSpeaking,
    isRecording,
    handleTranscriptChange,
    sampleWords
  );

  const reportDuration = useCallback(() => {
    if (recordingStartRef.current !== null) {
      const elapsed = Math.round((Date.now() - recordingStartRef.current) / 1000);
      onRecordingDurationChange?.(elapsed);
      recordingStartRef.current = null;
    }
  }, [onRecordingDurationChange]);

  // The live-recording path completes once the whole sample has been transcribed.
  useEffect(() => {
    if (micState === 'recording' && revealedWords >= sampleWords.length) {
      setMicState('done');
      stopMonitoring();
      reportDuration();
    }
  }, [micState, revealedWords, sampleWords.length, stopMonitoring, reportDuration]);

  const handleAutofill = useCallback(() => {
    if (autofilled) return;
    trackOnce('transcript_autofill', FieldType.Button);
    // Drop the full sample transcript in without recording so the user can
    // continue and preview the generated note. Continue unlocks because
    // revealedWords > 0.
    handleTranscriptChange(sampleWords.join(' '));
    setAutofilled(true);
  }, [autofilled, handleTranscriptChange, sampleWords, trackOnce]);

  // The auto-fill CTA unmounts on success, so move focus to the confirmation
  // (a live region) — keyboard users keep their place and screen readers
  // announce that the transcript is filled and Continue is now enabled.
  useEffect(() => {
    if (autofilled) autofillDoneRef.current?.focus();
  }, [autofilled]);

  const handleStart = useCallback(async () => {
    const granted = await mic.requestPermission();
    if (!granted) {
      // Browser-level permission prompt was dismissed / denied. The button
      // disables itself for the remainder of the session — that's a visible
      // dead-end for the user, so track it as a CRITICAL error.
      trackError({
        step: OnboardingErrorStep.MIC_TEST,
        errorType: ErrorType.PERMISSION_DENIED,
        errorMessage: 'Microphone permission denied or dismissed',
        severity: ErrorSeverity.CRITICAL,
      });
      return;
    }
    // Mic setup is a one-time engagement signal — the user can re-record,
    // but we only want a single `field_selected` event for "they actually
    // started the mic test".
    trackOnce('microphone_setup', FieldType.Button);
    sim.reset();
    setAutofilled(false);
    setRevealedWords(0);
    recordingStartRef.current = Date.now();
    setMicState('recording');
    mic.startMonitoring();
  }, [mic, sim, trackOnce, trackError]);

  const handleStop = () => {
    setMicState('done');
    stopMonitoring();
    reportDuration();
  };

  // Live-transcription (option-6) treatment: auto-play the transcript so the
  // preview types itself out, then present a single "Create my first note" CTA
  // instead of the auto-fill / own-voice cards.
  const handleTranscriptChangeRef = useRef(handleTranscriptChange);
  handleTranscriptChangeRef.current = handleTranscriptChange;
  useEffect(() => {
    if (!liveTranscriptionEnabled) return;
    const words = sampleWords;
    if (words.length === 0) return;

    let i = 0;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    handleTranscriptChangeRef.current('');

    const intervalMs = LIVE_TRANSCRIPTION_PLAYBACK_MS / words.length;
    const startId = setTimeout(() => {
      intervalId = setInterval(() => {
        i += 1;
        handleTranscriptChangeRef.current(words.slice(0, i).join(' '));
        if (i >= words.length && intervalId) {
          clearInterval(intervalId);
        }
      }, intervalMs);
    }, LIVE_TRANSCRIPTION_START_DELAY_MS);

    return () => {
      clearTimeout(startId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [liveTranscriptionEnabled, sampleWords]);

  if (liveTranscriptionEnabled) {
    const transcriptComplete =
      sampleWords.length > 0 && revealedWords >= sampleWords.length;
    const transcribing = requireRecordingToContinue && !transcriptComplete;
    const progress = sampleWords.length > 0 ? revealedWords / sampleWords.length : 0;
    return (
      <OnboardingStepShell
        footer={
          <div className="flex w-full flex-col gap-2">
            {/* While the sample transcribes, the CTA doubles as a determinate
                progress bar — a fill tracks words revealed and the label reads
                "Transcribing…" so the wait is explained, not just disabled. */}
            <Button
              size="lg"
              onClick={onContinue}
              disabled={transcribing}
              aria-busy={transcribing}
              className="relative w-full overflow-hidden"
            >
              {transcribing && (
                <span
                  aria-hidden
                  className="absolute inset-0 origin-left bg-background-button-primary/20 transition-transform duration-300 ease-out"
                  style={{ transform: `scaleX(${progress})` }}
                />
              )}
              <span className="relative">
                {transcribing
                  ? t('signup.micTestTranscribingCta')
                  : t('signup.micTestCreateFirstNoteCta')}
              </span>
            </Button>
            <Button
              size="lg"
              type="button"
              variant="ghost"
              onClick={onSkip}
              className="w-full"
            >
              {t('signup.skip')}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <TypographyH2 className="text-balance">
            {(() => {
              if (!specialtyName || !isEnglishMicTestLocale(locale)) {
                return t('signup.micTestTitlePersonalisedGeneric');
              }
              const title = t('signup.micTestTitlePersonalised', {
                specialty: specialtyName,
              });
              const idx = title.indexOf(specialtyName);
              if (idx === -1) return title;
              return (
                <>
                  {title.slice(0, idx)}
                  <span className="text-accent-forest">{specialtyName}</span>
                  {title.slice(idx + specialtyName.length)}
                </>
              );
            })()}
          </TypographyH2>
          <TypographyP className="text-text-secondary">
            {t('signup.micTestSubtitlePersonalised')}
          </TypographyP>
        </div>
      </OnboardingStepShell>
    );
  }

  if (micState === 'done') {
    return (
      <MicTestReadyState
        title={t('signup.micTestReadyTitle')}
        subtitle={t('signup.micTestReadySubtitle')}
        ctaLabel={t('signup.micTestDoneCta')}
        onContinue={onContinue}
      />
    );
  }

  return (
    <OnboardingStepShell
      footer={
        <div className="flex w-full flex-col gap-2">
          <Button
            size="lg"
            onClick={onContinue}
            disabled={requireRecordingToContinue && revealedWords === 0}
            className="w-full"
          >
            {t('common.continue')}
          </Button>
          <Button
            size="lg"
            type="button"
            variant="ghost"
            onClick={onSkip}
            className="w-full"
          >
            {t('signup.skip')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <TypographyH2>{t('signup.micTestTitle')}</TypographyH2>
        <TypographyP className="text-text-secondary">
          {t('signup.micTestSubtitleAutofill')}
        </TypographyP>
      </div>

      {/* Primary action — auto-fill the transcript (no mic). One tap drops in a
          sample consult so the user can continue and preview their first note. */}
      <div className="mt-8">
        {isRecording ? (
          <button
            type="button"
            onClick={handleStop}
            className="flex w-full items-center gap-4 rounded-2xl border border-validation-destructive/30 bg-validation-destructive/5 p-5 text-left transition-colors hover:bg-validation-destructive/10"
          >
            <div className="flex size-15 shrink-0 items-center justify-center rounded-full bg-validation-destructive text-text-primary-invert shadow-[0_8px_20px_-8px_rgba(0,0,0,0.45)]">
              <Square className="size-5" />
            </div>
            <div className="flex-1">
              <TypographyP2 className="text-base font-medium text-text-primary">
                {t('signup.micTestListening')}
              </TypographyP2>
            </div>
            <AudioBars level={mic.level} />
          </button>
        ) : autofilled ? (
          <div
            ref={autofillDoneRef}
            role="status"
            aria-live="polite"
            tabIndex={-1}
            className="flex w-full items-center gap-4 rounded-2xl border border-validation-success/30 bg-validation-success-muted p-5 text-left focus:outline-none"
          >
            <div className="flex size-15 shrink-0 items-center justify-center rounded-full bg-validation-success text-text-primary-invert">
              <Check className="size-6" />
            </div>
            <div className="flex-1">
              <TypographyP2 className="text-base font-medium text-text-primary">
                {t('signup.micTestAutofillDone')}
              </TypographyP2>
            </div>
          </div>
        ) : (
          <button
            type="button"
            data-testid="mic-test-button"
            onClick={handleAutofill}
            className="flex w-full items-center gap-4 rounded-2xl border border-background-button-primary/15 bg-background-button-primary/[0.05] p-5 text-left transition-colors hover:border-background-button-primary/40 hover:bg-background-button-primary/[0.08]"
          >
            <div className="flex size-15 shrink-0 items-center justify-center rounded-full bg-background-button-primary text-text-primary-invert shadow-[0_8px_20px_-8px_rgba(0,0,0,0.45)]">
              <SkipForward className="size-6 fill-current" />
            </div>
            <div className="flex-1">
              <TypographyP2 className="text-base font-medium text-text-primary">
                {t('signup.micTestAutofillCta')}
              </TypographyP2>
              <TypographyCaption className="mt-0.5 text-text-tertiary">
                {t('signup.micTestAutofillCtaHelper')}
              </TypographyCaption>
            </div>
            <ArrowRight className="size-5 shrink-0 text-background-button-primary/60 rtl:-scale-x-100" />
          </button>
        )}
      </div>

      {/* Secondary action — record with your own voice (asks for mic access).
          Rests as a neutral card and reveals the green "record" affordance on
          hover. Hidden once the user is recording. */}
      {micState === 'idle' && (
        <>
          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <TypographyCaption className="text-text-tertiary">
              {t('signup.micTestOr')}
            </TypographyCaption>
            <div className="h-px flex-1 bg-border" />
          </div>

          <button
            type="button"
            onClick={() => void handleStart()}
            disabled={mic.permission === 'denied'}
            className="group flex w-full items-center gap-3 rounded-xl border border-border bg-background-secondary px-4 py-3 text-left transition-colors hover:border-accent-forest/30 hover:bg-accent-forest/[0.05] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-background-tertiary text-text-secondary transition-colors group-hover:bg-accent-forest/15 group-hover:text-accent-forest">
              <Mic className="size-4" />
            </div>
            <TypographyP2 className="flex-1 text-sm font-medium text-text-primary">
              {t('signup.micTestUseOwnVoice')}
            </TypographyP2>
            <ArrowRight className="size-4 shrink-0 text-text-tertiary transition-colors group-hover:text-accent-forest/70 rtl:-scale-x-100" />
          </button>
        </>
      )}
    </OnboardingStepShell>
  );
}
